import { useMemo } from 'react';

import { Column, ColumnDef } from '@tanstack/react-table';

interface ColumnSizingResult {
  tableMinWidth: number | undefined;
  getWidth: <T>(column: Column<T, unknown>) => number | undefined;
  getMinWidth: <T>(column: Column<T, unknown>) => number | undefined;
  getMaxWidth: <T>(column: Column<T, unknown>) => number | undefined;
}

/**
 * Hook for column sizing using TanStack native size/minSize/maxSize with meta.preset.
 *
 * For FLEX columns: Returns undefined width, letting the browser's
 * table-layout: fixed naturally allocate remaining space.
 * CSS min-width/max-width constraints are applied via getMinWidth/getMaxWidth.
 *
 * tableMinWidth: Ensures the table never shrinks below the sum of all column
 * minimum widths. This prevents FLEX columns from collapsing to 0 when the
 * container is narrow - instead, horizontal scroll is enabled.
 */
export function useColumnSizing<T>(columns: ColumnDef<T, unknown>[]): ColumnSizingResult {
  const hasPresets = useMemo(() => {
    return columns.some((col) => col.meta?.preset !== undefined || typeof col.size === 'number');
  }, [columns]);

  // Whether any visible column uses the FLEX preset
  const hasFlex = useMemo(() => {
    return columns.some((col) => col.meta?.preset === 'FLEX');
  }, [columns]);

  // Calculate total minimum width for the table
  // This ensures FLEX columns don't collapse when container shrinks
  const tableMinWidth = useMemo(() => {
    if (!hasPresets) {
      return undefined;
    }

    return columns.reduce((sum, col) => {
      // FLEX columns: use minSize (check FIRST because TanStack adds default size: 150)
      if (col.meta?.preset === 'FLEX') {
        return sum + (col.minSize ?? 0);
      }
      // Fixed columns (SM, MD, LG): use the defined size
      if (typeof col.size === 'number') {
        return sum + col.size;
      }
      return sum;
    }, 0);
  }, [columns, hasPresets]);

  const getWidth = <TData>(column: Column<TData, unknown>): number | undefined => {
    const def = column.columnDef;

    // FLEX: no width - browser distributes remaining space via table-layout: fixed
    if (def.meta?.preset === 'FLEX') {
      return undefined;
    }

    // Fixed preset (SM, MD, LG)
    if (typeof def.size === 'number') {
      return def.size;
    }

    return undefined;
  };

  const getMinWidth = <TData>(column: Column<TData, unknown>): number | undefined => {
    return column.columnDef.minSize;
  };

  const getMaxWidth = <TData>(column: Column<TData, unknown>): number | undefined => {
    // When no FLEX column is visible, skip maxWidth so fixed columns can
    // expand to fill the table width under table-layout: fixed
    if (!hasFlex) {
      return undefined;
    }
    return column.columnDef.maxSize;
  };

  return { tableMinWidth, getWidth, getMinWidth, getMaxWidth };
}
